import React from 'react';
import {
  GET_QUOTES_LIST_FROM_PC_getQuotesInformation,
  GET_QUOTES_LIST_FROM_PC_getQuotesInformation_quotesList
} from './graphql/__generated__/GET_QUOTES_LIST_FROM_PC';
import { CardLayout } from '../../components/cardLayout/CardLayout';
import { Box, ResponsiveContext } from 'grommet';
import {
  BigBoldText,
  Data,
  DataGroup,
  DataText,
  ErrorBox,
  OuterBox,
  OuterBoxx,
  SmallText,
  TextData
} from './QuotesPageStyles';
import { formatDate, formatMoney } from '../../util/formatters';
import { colors } from '../../themes/default.theme';
import { useParams } from '@reach/router';
import NoPolicyorAccount from '../../assets/images/No_policies.png';
import {
  BeachAccessOutlined,
  BusinessOutlined,
  EmojiTransportationOutlined,
  HomeOutlined,
  LocalShippingOutlined
} from '@material-ui/icons';
import usePagination from '../../util/usePagination';

export default ({
  quotesList,
  __typename
}: GET_QUOTES_LIST_FROM_PC_getQuotesInformation) => {
  const params = useParams();
  const accountNumber = params.accountNumber;
  const sourceSystem = params.lob;
  let urlLob = sourceSystem || 'commercial';
  if (!sourceSystem && quotesList && quotesList?.length > 0) {
    const isPersonalLines = [
      'personal auto line',
      'homeowners line',
      'personal umbrella line'
    ];
    const product = quotesList[0]?.product?.toLowerCase();
    if (product && isPersonalLines.includes(product)) {
      urlLob = 'personal';
    }
  }
  const columns = [
    {
      property: 'create',
      header: <DataText>Create Date</DataText>
    },
    {
      property: 'product',
      header: <DataText>Product</DataText>
    },
    {
      property: 'quotes',
      header: <DataText>Quote #</DataText>
    },
    {
      property: 'premium',
      header: <DataText>Premium</DataText>
    },
    {
      property: 'status',
      header: <DataText>Status</DataText>
    }
  ];
  const allowedStatus = ['quoted', 'draft', 'uw referral'];
  const quotesPremium = (premium: null | string): boolean => {
    if (premium === null || premium === '-' || premium === '0') {
      return false;
    } else return true;
  };
  const getLOBIcon = (product: string) => {
    if (product.toLowerCase().includes('auto')) {
      return <LocalShippingOutlined />;
    }
    if (product.toLowerCase().includes('home')) {
      return <HomeOutlined />;
    }
    if (product.toLowerCase().includes('business')) {
      return <BusinessOutlined />;
    }
    if (product.toLowerCase().includes('umbrella')) {
      return <BeachAccessOutlined />;
    } else {
      return <EmojiTransportationOutlined />;
    }
  };
  const getQuoteNumber = (
    quote: GET_QUOTES_LIST_FROM_PC_getQuotesInformation_quotesList
  ) => {
    const nonClickableQuotes = ['uw referral', 'declined']
    return (
      <>
        {quote && quote?.status && !nonClickableQuotes.includes(quote.status.toLowerCase()) ? (
          <TextData
            onClick={() => {
              window.location.href = `/quotes/${urlLob}/${quote?.jobNumber}/summary`;
            }}
            style={{ color: colors.darkBlue2, cursor: 'pointer' }}
          >
            {quote?.jobNumber}
          </TextData>
        ) : (
          <TextData>{quote?.jobNumber}</TextData>
        )}
      </>
    );
  };

  const [paginatedQuoteList, pageSelector] = usePagination({
    accountNumber,
    items: quotesList && quotesList?.length > 0 ? quotesList : []
  });

  return (
    <>
      {paginatedQuoteList && paginatedQuoteList.length > 0 ? (
        <CardLayout title="Quotes Information">
          <ResponsiveContext.Consumer>
            {responsive => (
              <Box background="white">
                <Box
                  fill={
                    responsive === 'small' || responsive === 'medium'
                      ? false
                      : 'horizontal'
                  }
                  responsive
                  overflow="auto"
                >
                  {' '}
                  {paginatedQuoteList && paginatedQuoteList.length > 0 ? (
                    <>
                      <Box direction="row">
                        {columns?.map(column => (
                          <Data key={column?.property}>{column.header}</Data>
                        ))}
                      </Box>
                      <Box>
                        {paginatedQuoteList &&
                          paginatedQuoteList.length > 0 &&
                          paginatedQuoteList.map((quote, index) => {
                            if (
                              quote?.status &&
                              allowedStatus.includes(
                                quote?.status?.toLowerCase()
                              )
                            ) {
                              return (
                                <Box
                                  border={
                                    index === paginatedQuoteList.length
                                      ? { side: 'bottom', color: 'white' }
                                      : { side: 'bottom', color: '#D9DFEB' }
                                  }
                                  key={quote?.jobNumber}
                                >
                                  <OuterBox>
                                    <DataGroup>
                                      <TextData>
                                        {quote?.createTime
                                          ? formatDate(
                                              quote.createTime.split('T')[0]
                                            )
                                          : '-'}
                                      </TextData>
                                      <TextData>
                                        {quote &&
                                          quote?.product &&
                                          getLOBIcon(quote?.product)}
                                        &nbsp;
                                        {quote?.product}
                                      </TextData>
                                      {getQuoteNumber(quote)}
                                      <TextData>
                                        {quote?.totalPremium &&
                                        quotesPremium(quote?.totalPremium) ? (
                                          formatMoney(quote.totalPremium)
                                        ) : (
                                          <>-</>
                                        )}
                                      </TextData>
                                      <TextData>{quote?.status}</TextData>
                                    </DataGroup>
                                  </OuterBox>
                                </Box>
                              );
                            }
                          })}
                        <Box align="end">{pageSelector}</Box>
                      </Box>
                    </>
                  ) : (
                    <p>There are no quotes meeting these filters.</p>
                  )}
                </Box>
              </Box>
            )}
          </ResponsiveContext.Consumer>
        </CardLayout>
      ) : (
        <ErrorBox>
          <OuterBoxx>
            <img
              height="250px"
              width="250px"
              src={NoPolicyorAccount}
              alt="No Open Quotes Exists"
            />
            <BigBoldText>
              No Open Quotes Available for Account {accountNumber}
            </BigBoldText>
            <SmallText>
              If a quote was just opened, check back shortly!
            </SmallText>
          </OuterBoxx>
        </ErrorBox>
      )}
    </>
  );
};
